import React, { useState } from "react";
import { useCart } from "../context/CartContext";
import { Link } from "react-router-dom";
import { ShoppingBag, Lock, CheckCircle, ChevronLeft, ChevronRight, Calendar, AlertCircle, Tag, X } from "lucide-react";
import { ORDERS_CLOSED, ORDERS_RETURN_DATE } from "../lib/menuData";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { supabase } from "../supabaseClient";

// ─── Pickup scheduling ─────────────────────────────────────────
// Customers pick any upcoming Saturday or Sunday from a calendar,
// then choose a time window. The earliest selectable date respects
// the Thursday 5:00 PM CT cutoff (orders after that start next weekend).
const TIME_SLOTS = [
  { slot: "am", label: "10:00 AM – 12:00 PM" },
  { slot: "pm", label: "4:00 PM – 6:00 PM" },
];

// Earliest Saturday a customer may pick, honoring the Thursday 5 PM CT cutoff.
function getEarliestSaturday() {
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Chicago" }));
  const day = now.getDay();
  const hour = now.getHours();
  const isAfterCutoff = (day === 4 && hour >= 17) || day === 5 || day === 6 || day === 0;
  const daysUntilSat = day === 0 ? -1 : (6 - day + 7) % 7;
  const sat = new Date(now);
  sat.setDate(now.getDate() + daysUntilSat);
  if (isAfterCutoff) sat.setDate(sat.getDate() + 7);
  return sat;
}

const toInputDate = (d) => {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

// "sat" | "sun" | null  (parsed at noon to avoid timezone off-by-one)
function weekendPrefix(dateStr) {
  if (!dateStr) return null;
  const day = new Date(`${dateStr}T12:00:00`).getDay();
  return day === 6 ? "sat" : day === 0 ? "sun" : null;
}

function formatPickupDate(dateStr) {
  if (!dateStr) return "";
  return new Date(`${dateStr}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric",
  });
}

const timeLabelForWindow = (id) => {
  const s = TIME_SLOTS.find((t) => id?.endsWith(`-${t.slot}`));
  return s ? s.label : "";
};

// ─── Update weekly as needed ───────────────────────────────────
// Add window IDs here to mark them as unavailable
// Options: "sat-am", "sat-pm", "sun-am", "sun-pm"
const UNAVAILABLE_WINDOWS = [];
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function OrderSummary({ items, totalPrice, totalMacarons, appliedPromo }) {
  const discountAmount = appliedPromo
    ? +(totalPrice * appliedPromo.discountPercent / 100).toFixed(2)
    : 0;
  const finalPrice = totalPrice - discountAmount;

  return (
    <div className="card p-5 mb-6">
      <h3 className="font-display text-lg text-sage-800 mb-4">Order Summary</h3>
      {items.length === 0 ? (
        <p className="text-gray-400 text-sm text-center py-4">Your cart is empty</p>
      ) : (
        <>
          <div className="space-y-2 mb-4">
            {items.map((item) => (
              <div key={item.id} className="flex items-center justify-between text-sm">
                <span className="text-gray-600">
                  {item.name}{" "}
                  <span className="text-gray-400">× {item.qty}</span>
                </span>
                <span className="font-bold text-sage-700">
                  ${(item.price * item.qty).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
          <div className="border-t border-cream-200 pt-3">
            {appliedPromo ? (
              <>
                <div className="flex justify-between mb-1.5 text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="text-gray-500">${totalPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-2.5 text-sm">
                  <span className="text-green-600 font-bold flex items-center gap-1">
                    <Tag size={11} /> {appliedPromo.code} ({appliedPromo.discountPercent}% off)
                  </span>
                  <span className="text-green-600 font-bold">−${discountAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between border-t border-cream-200 pt-2.5">
                  <span className="font-bold text-gray-700">Total</span>
                  <span className="font-display text-xl text-sage-800">${finalPrice.toFixed(2)}</span>
                </div>
              </>
            ) : (
              <div className="flex justify-between mb-1">
                <span className="font-bold text-gray-700">Total</span>
                <span className="font-display text-xl text-sage-800">${totalPrice.toFixed(2)}</span>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {totalMacarons} macarons total · Weekend pickup · No delivery
          </p>
        </>
      )}
    </div>
  );
}

function TermsBox() {
  return (
    <div className="bg-cream-50 border border-cream-200 rounded-xl p-4 max-h-48 overflow-y-auto text-xs text-gray-500 leading-relaxed space-y-3">
      <div>
        <p className="font-bold text-sage-700 mb-0.5">Payment Policy</p>
        <p>Full payment is required at the time of ordering. Your order is not confirmed until payment is received.</p>
      </div>
      <div>
        <p className="font-bold text-sage-700 mb-0.5">Cancellations & Refunds</p>
        <p>Due to the made-to-order nature of our products, cancellations or refund requests are not accepted after an order is placed.</p>
      </div>
      <div>
        <p className="font-bold text-sage-700 mb-0.5">Cottage Food Notice</p>
        <p>These products are made in a home kitchen and are not inspected by the Texas Department of State Health Services, as permitted under the Texas Cottage Food Law.</p>
      </div>
      <div>
        <p className="font-bold text-sage-700 mb-0.5">Allergen Notice</p>
        <p>Our macarons are 100% gluten free and made in a dedicated gluten-free kitchen. Products contain eggs, dairy, and tree nuts (almond flour), and may contain soy. While every care is taken, please order at your own discretion if you have additional allergies.</p>
      </div>
      <div>
        <p className="font-bold text-sage-700 mb-0.5">Liability Disclaimer</p>
        <p>By purchasing and consuming our products, you agree that Stewart's Sweet Shells is not legally liable for any allergic reactions that may result from consumption. Customers assume full responsibility for verifying ingredients and determining whether products are safe for their dietary needs.</p>
      </div>
    </div>
  );
}

// ── Stripe payment form ────────────────────────────────────────
function StripePaymentForm({ totalPrice, form, items, onSuccess, onBack, pickupLabel, appliedPromo }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !ready) return;

    setProcessing(true);
    setError("");

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin + "/order",
      },
      redirect: "if_required",
    });

    if (confirmError) {
      setError(confirmError.message ?? "Payment failed. Please try again.");
      setProcessing(false);
      return;
    }

    // Payment succeeded — save order to Supabase
    try {
      const { error: dbError } = await supabase.from("orders").insert({
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        phone: form.phone,
        items: items.map((i) => ({ name: i.name, qty: i.qty, price: i.price, flavorId: i.flavorId, count: i.count })),
        total: totalPrice,
        pickup_window: form.pickupWindow,
        pickup_label: pickupLabel,
        special_instructions: form.specialInstructions || null,
        stripe_payment_id: paymentIntent?.id ?? null,
        status: "pending",
        promo_code: appliedPromo?.code ?? null,
        discount_percent: appliedPromo?.discountPercent ?? null,
      });

      if (dbError) {
        console.error("Supabase error:", dbError);
      }
    } catch (err) {
      console.error("Order save error:", err);
    }

    // Send notification email
    try {
      await fetch("/api/send-order-notification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order: {
            first_name: form.firstName,
            last_name: form.lastName,
            email: form.email,
            phone: form.phone,
            items: items.map((i) => ({ name: i.name, qty: i.qty, price: i.price, flavorId: i.flavorId, count: i.count })),
            total: totalPrice,
            pickup_window: form.pickupWindow,
            pickup_label: pickupLabel,
            special_instructions: form.specialInstructions,
            stripe_payment_id: paymentIntent?.id ?? null,
            promo_code: appliedPromo?.code ?? null,
            discount_percent: appliedPromo?.discountPercent ?? null,
          },
        }),
      });
    } catch (err) {
      console.error("Notification email failed:", err);
    }

    onSuccess();
  };

  return (
    <form onSubmit={handleSubmit} className="card p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl text-sage-800">Secure Payment</h2>
        <div className="flex items-center gap-1 text-xs text-gray-400">
          <Lock size={12} /> Stripe
        </div>
      </div>

      <PaymentElement onReady={() => setReady(true)} />

      {!ready && (
        <p className="text-xs text-gray-400 text-center">Loading payment form...</p>
      )}

      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <AlertCircle size={11} /> {error}
        </p>
      )}

      <div className="text-xs text-gray-400 flex items-start gap-2 bg-cream-50 p-3 rounded-lg">
        <Lock size={12} className="mt-0.5 flex-shrink-0 text-sage-500" />
        Your payment is encrypted and processed by Stripe. We never store card information.
      </div>

      <button
        type="submit"
        disabled={!stripe || !ready || processing}
        className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <Lock size={16} />
        {processing ? "Processing..." : `Pay $${totalPrice.toFixed(2)} Securely`}
      </button>

      <button
        type="button"
        onClick={onBack}
        className="w-full text-center text-sm text-gray-400 hover:text-sage-600 flex items-center justify-center gap-1 transition-colors"
      >
        <ChevronLeft size={14} /> Back to your info
      </button>
    </form>
  );
}

// ── Pickup calendar (only Saturdays & Sundays selectable) ──────
const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

function PickupCalendar({ value, min, onSelect }) {
  const minDate = new Date(`${min}T12:00:00`);
  const initial = value ? new Date(`${value}T12:00:00`) : minDate;
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());

  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  const startWeekday = firstOfMonth.getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const atMinMonth =
    viewYear === minDate.getFullYear() && viewMonth === minDate.getMonth();

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const stepMonth = (delta) => {
    const next = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  };

  return (
    <div className="border border-cream-300 rounded-lg p-3 bg-white">
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => stepMonth(-1)}
          disabled={atMinMonth}
          className="w-7 h-7 rounded-full flex items-center justify-center text-sage-700 hover:bg-sage-50 disabled:opacity-25 disabled:cursor-not-allowed"
          aria-label="Previous month"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="text-sm font-bold text-sage-800">
          {firstOfMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
        </span>
        <button
          type="button"
          onClick={() => stepMonth(1)}
          className="w-7 h-7 rounded-full flex items-center justify-center text-sage-700 hover:bg-sage-50"
          aria-label="Next month"
        >
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAY_LABELS.map((d, i) => (
          <div key={i} className="text-center text-[10px] font-bold text-gray-400">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const date = new Date(viewYear, viewMonth, d);
          const dow = date.getDay();
          const iso = toInputDate(date);
          const selectable = (dow === 0 || dow === 6) && iso >= min;
          const selected = iso === value;
          return (
            <button
              key={i}
              type="button"
              disabled={!selectable}
              onClick={() => onSelect(iso)}
              className={`h-8 rounded-md text-sm transition-colors ${
                selected
                  ? "bg-sage-600 text-white font-bold"
                  : selectable
                  ? "text-sage-800 hover:bg-sage-100"
                  : "text-gray-300 cursor-not-allowed"
              }`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Order component ───────────────────────────────────────
export default function Order() {
  const { items, totalPrice, totalMacarons, dispatch } = useCart();
  const [step, setStep] = useState(1);
  const [clientSecret, setClientSecret] = useState("");
  const [loadingIntent, setLoadingIntent] = useState(false);
  const [intentError, setIntentError] = useState("");
  const minPickupDate = toInputDate(getEarliestSaturday());
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    pickupDate: minPickupDate,
    pickupWindow: "",
    specialInstructions: "",
  });
  const [errors, setErrors] = useState({});
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);

  const discountAmount = appliedPromo
    ? +(totalPrice * appliedPromo.discountPercent / 100).toFixed(2)
    : 0;
  const discountedPrice = totalPrice - discountAmount;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: "" });
  };

  const selectPickupDate = (value) => {
    const prefix = weekendPrefix(value);
    setForm((prev) => {
      const next = { ...prev, pickupDate: value };
      // Reset the time window if it no longer belongs to the chosen day.
      if (prev.pickupWindow && !prev.pickupWindow.startsWith(prefix)) {
        next.pickupWindow = "";
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, pickupDate: "", pickupWindow: "" }));
  };

  const handleApplyPromo = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) return;
    setPromoLoading(true);
    setPromoError("");
    try {
      const res = await fetch("/api/validate-promo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, totalMacarons }),
      });
      const data = await res.json();
      if (data.error) {
        setPromoError(data.error);
        setAppliedPromo(null);
      } else {
        setAppliedPromo({ code, discountPercent: data.discountPercent });
      }
    } catch {
      setPromoError("Could not validate promo code. Please try again.");
    } finally {
      setPromoLoading(false);
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = "Required";
    if (!form.lastName.trim()) errs.lastName = "Required";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) errs.email = "Valid email required";
    if (!form.phone.trim()) errs.phone = "Required";
    if (!form.pickupDate) {
      errs.pickupDate = "Please select a pickup date";
    } else if (!weekendPrefix(form.pickupDate)) {
      errs.pickupDate = "Pickup is available on Saturdays and Sundays only";
    } else if (form.pickupDate < minPickupDate) {
      errs.pickupDate = "Please choose an upcoming weekend";
    }
    if (!form.pickupWindow) errs.pickupWindow = "Please select a pickup time";
    if (!agreedToTerms) errs.terms = "Please review and accept the terms to continue";
    return errs;
  };

  const handleInfoSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoadingIntent(true);
    setIntentError("");
    try {
      const res = await fetch("/api/create-payment-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: items.map((i) => ({ qty: i.qty, count: i.count })),
          orderData: form,
          promoCode: appliedPromo?.code ?? null,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setClientSecret(data.clientSecret);
      setStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setIntentError("Unable to initialize payment. Please try again.");
    } finally {
      setLoadingIntent(false);
    }
  };

  const handlePaymentSuccess = () => {
    setStep(3);
    dispatch({ type: "CLEAR" });
  };

  if (ORDERS_CLOSED && step !== 3) {
    return (
      <div className="pt-32 min-h-screen flex flex-col items-center justify-center px-4 text-center">
        <ShoppingBag size={48} className="mx-auto mb-4 opacity-30" />
        <h2 className="font-display text-2xl text-sage-800 mb-2">Orders Closed</h2>
        <p className="text-gray-500 text-sm mb-6">We will be taking orders again on <strong>{ORDERS_RETURN_DATE}</strong>. Check back soon!</p>
        <Link to="/" className="btn-primary">Back to Home</Link>
      </div>
    );
  }

  if (items.length === 0 && step !== 3) {
    return (
      <div className="pt-32 min-h-screen flex flex-col items-center justify-center px-4">
        <ShoppingBag size={48} className="mx-auto mb-4 opacity-30" />
        <h2 className="font-display text-2xl text-sage-800 mb-2">Your cart is empty</h2>
        <p className="text-gray-500 text-sm mb-6">Browse menu to get started!</p>
        <Link to="/menu" className="btn-primary">Browse Menu</Link>
      </div>
    );
  }

  if (step === 3) {
    return (
      <div className="pt-32 min-h-screen flex flex-col items-center justify-center px-4 text-center">
        <CheckCircle size={56} className="text-sage-600 mb-4" />
        <h1 className="font-display text-4xl text-sage-900 mb-2">Order Confirmed!</h1>
        <p className="font-handwrite text-sage-500 text-xl mb-4">Thank you, {form.firstName}!</p>
        <p className="text-gray-500 text-sm max-w-sm mb-2">
          A confirmation has been sent to <strong>{form.email}</strong>.
        </p>
        <div className="bg-cream-100 rounded-xl px-6 py-4 mb-8 text-sm text-sage-800 flex items-center gap-2">
          <Calendar size={16} className="text-sage-600 flex-shrink-0" />
          <strong>{formatPickupDate(form.pickupDate)} · {timeLabelForWindow(form.pickupWindow)}</strong>
        </div>
        <Link to="/" className="btn-primary">Back to Home</Link>
      </div>
    );
  }

  return (
    <div className="pt-24 min-h-screen bg-cream-50 pb-20">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="text-center py-8">
          <h1 className="font-display text-4xl text-sage-900 mb-1">Checkout</h1>
          <p className="text-gray-500 text-sm">Guest checkout — no account needed.</p>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-3 mb-8">
          {["Your Info", "Payment", "Confirmed"].map((label, i) => (
            <React.Fragment key={label}>
              <div className={`flex items-center gap-1.5 text-xs font-bold ${step === i + 1 ? "text-sage-700" : step > i + 1 ? "text-sage-500" : "text-gray-300"}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${step === i + 1 ? "bg-sage-600 text-white" : step > i + 1 ? "bg-sage-300 text-white" : "bg-gray-200 text-gray-400"}`}>
                  {i + 1}
                </span>
                <span className="hidden sm:inline">{label}</span>
              </div>
              {i < 2 && <div className={`flex-1 h-px max-w-12 ${step > i + 1 ? "bg-sage-300" : "bg-gray-200"}`} />}
            </React.Fragment>
          ))}
        </div>

        <OrderSummary
          items={items}
          totalPrice={totalPrice}
          totalMacarons={totalMacarons}
          appliedPromo={appliedPromo}
        />

        {/* Step 1: Contact + Pickup */}
        {step === 1 && (
          <form onSubmit={handleInfoSubmit} className="card p-6 space-y-4">
            <h2 className="font-display text-xl text-sage-800">Your Information</h2>
            <div className="grid grid-cols-2 gap-3">
              {[["firstName", "First Name", "Jane"], ["lastName", "Last Name", "Smith"]].map(([name, label, placeholder]) => (
                <div key={name}>
                  <label className="text-xs font-bold text-gray-600 block mb-1">{label} *</label>
                  <input name={name} value={form[name]} onChange={handleChange} placeholder={placeholder}
                    className={`w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sage-300 ${errors[name] ? "border-red-300" : "border-cream-300"}`} />
                  {errors[name] && <p className="text-xs text-red-500 mt-1">{errors[name]}</p>}
                </div>
              ))}
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600 block mb-1">Email Address *</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="jane@example.com"
                className={`w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sage-300 ${errors.email ? "border-red-300" : "border-cream-300"}`} />
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600 block mb-1">Phone Number *</label>
              <input name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="(555) 000-0000"
                className={`w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sage-300 ${errors.phone ? "border-red-300" : "border-cream-300"}`} />
              {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600 block mb-1">
                <Calendar size={12} className="inline mr-1" />
                Pickup Date * <span className="text-gray-400 font-normal">(Saturdays & Sundays)</span>
              </label>
              <PickupCalendar
                value={form.pickupDate}
                min={minPickupDate}
                onSelect={selectPickupDate}
              />
              {errors.pickupDate && <p className="text-xs text-red-500 mt-1">{errors.pickupDate}</p>}

              {weekendPrefix(form.pickupDate) && (
                <div className="mt-3">
                  <label className="text-xs font-bold text-gray-600 block mb-1.5">
                    Pickup Time * <span className="text-gray-400 font-normal">({formatPickupDate(form.pickupDate)})</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {TIME_SLOTS.map((s) => {
                      const id = `${weekendPrefix(form.pickupDate)}-${s.slot}`;
                      const unavailable = UNAVAILABLE_WINDOWS.includes(id);
                      const selected = form.pickupWindow === id;
                      return (
                        <button
                          key={s.slot}
                          type="button"
                          disabled={unavailable}
                          onClick={() => {
                            setForm((f) => ({ ...f, pickupWindow: id }));
                            setErrors((p) => ({ ...p, pickupWindow: "" }));
                          }}
                          className={`rounded-lg border px-3 py-2 text-sm font-bold transition-all ${
                            unavailable
                              ? "border-cream-200 text-gray-300 cursor-not-allowed line-through"
                              : selected
                              ? "border-sage-500 bg-sage-50 text-sage-800"
                              : "border-cream-300 text-gray-600 hover:border-sage-300"
                          }`}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                  {errors.pickupWindow && <p className="text-xs text-red-500 mt-1">{errors.pickupWindow}</p>}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-2">We will confirm your pickup window via email.</p>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600 block mb-1">Special Instructions</label>
              <textarea name="specialInstructions" value={form.specialInstructions} onChange={handleChange} rows={3}
                className="w-full border border-cream-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sage-300 resize-none"
                placeholder="Allergy notes, customization requests..." />
            </div>

            {/* Promo Code */}
            <div>
              <label className="text-xs font-bold text-gray-600 block mb-1">
                <Tag size={12} className="inline mr-1" />
                Promo Code <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <div className="flex gap-2">
                <input
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value.toUpperCase());
                    setPromoError("");
                    if (appliedPromo) setAppliedPromo(null);
                  }}
                  placeholder="Enter code"
                  disabled={!!appliedPromo}
                  className="flex-1 border border-cream-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sage-300 tracking-widest uppercase"
                />
                {appliedPromo ? (
                  <button
                    type="button"
                    onClick={() => { setAppliedPromo(null); setPromoCode(""); setPromoError(""); }}
                    className="px-3 py-2 text-sm border border-cream-300 rounded-lg text-gray-500 hover:text-red-400 hover:border-red-300 transition-colors flex items-center gap-1"
                  >
                    <X size={14} /> Remove
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleApplyPromo}
                    disabled={!promoCode.trim() || promoLoading}
                    className="px-4 py-2 text-sm bg-sage-600 text-white rounded-lg hover:bg-sage-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                  >
                    {promoLoading ? "Checking..." : "Apply"}
                  </button>
                )}
              </div>
              {promoError && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle size={11} /> {promoError}
                </p>
              )}
              {appliedPromo && (
                <p className="text-xs text-green-600 mt-1 font-bold">
                  ✓ {appliedPromo.discountPercent}% discount applied!
                </p>
              )}
            </div>

            {/* Terms & Conditions */}
            <div>
              <p className="text-xs font-bold text-gray-600 mb-2">Order Terms & Policies *</p>
              <TermsBox />
              <label className="flex items-start gap-3 mt-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => {
                    setAgreedToTerms(e.target.checked);
                    if (e.target.checked) setErrors((prev) => ({ ...prev, terms: "" }));
                  }}
                  className="mt-0.5 accent-sage-600 w-4 h-4 flex-shrink-0"
                />
                <span className="text-xs text-gray-500 leading-relaxed group-hover:text-gray-700 transition-colors">
                  I have read and agree to the order terms above.
                </span>
              </label>
              {errors.terms && (
                <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                  <AlertCircle size={11} /> {errors.terms}
                </p>
              )}
            </div>

            {intentError && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle size={11} /> {intentError}
              </p>
            )}

            <button type="submit" disabled={loadingIntent} className="btn-primary w-full disabled:opacity-60">
              {loadingIntent ? "Preparing payment..." : "Continue to Payment →"}
            </button>
          </form>
        )}

        {/* Step 2: Stripe Payment Element */}
        {step === 2 && clientSecret && (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: {
                theme: "stripe",
                variables: {
                  colorPrimary: "#4a7c59",
                  colorBackground: "#ffffff",
                  colorText: "#2f502a",
                  colorDanger: "#df1b41",
                  fontFamily: "Lato, sans-serif",
                  borderRadius: "8px",
                },
              },
            }}
          >
            <StripePaymentForm
              totalPrice={discountedPrice}
              form={form}
              items={items}
              onSuccess={handlePaymentSuccess}
              onBack={() => setStep(1)}
              pickupLabel={`${formatPickupDate(form.pickupDate)} · ${timeLabelForWindow(form.pickupWindow)}`}
              appliedPromo={appliedPromo}
            />
          </Elements>
        )}
      </div>
    </div>
  );
}
